import { type ChangeEvent, type MouseEvent, type ReactNode, useState } from 'react'

import { isCookieConsentDone, saveCookieConsent } from '@/repositories/cookieConsentRepository.ts'

const PopupHeader = ({ children }: { children: ReactNode }) => {
  return (
    <h1 className="text-white text-[24px] font-bold [font-family:'Inter-Bold',Helvetica] tracking-[-0.6px] leading-[36px]">
      {children}
    </h1>
  )
}

const PopupParagraph = ({ children }: { children: ReactNode }) => {
  return (
    <p className="text-white text-xs font-normal [font-family:'Inter-Regular',Helvetica] tracking-[-0.3px] leading-[18px]">
      {children}
    </p>
  )
}

const PopupLink = ({ children, href }: { children: ReactNode; href: string }) => {
  return (
    <a href={href} target="_blank" rel="noreferrer" className="tracking-[-0.10px] underline">
      {children}
    </a>
  )
}

const PopupButton = ({ onClick, children }: { onClick: (event: MouseEvent) => void; children: ReactNode }) => {
  return (
    <button
      type="button"
      className="bg-[#34495e] hover:bg-[#2c3e50] rounded-none px-4 py-3 h-auto [font-family:'Inter-Bold',Helvetica] font-bold text-white text-sm tracking-[-0.35px] leading-[20px]"
      onClick={onClick}
    >
      {children}
    </button>
  )
}

type CheckboxProps = {
  id: string
  checked: boolean
  label: string
  disabled?: boolean
  defaultValue?: string
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void
}

const PopupCheckbox = ({
  id,
  checked,
  label,
  disabled = false,
  defaultValue = 'false',
  onChange = () => {},
}: CheckboxProps) => {
  return (
    <label
      htmlFor={id}
      className="flex items-center gap-3 [font-family:'Inter-Regular',Helvetica] font-normal text-white text-sm tracking-[-0.35px] leading-[21px]"
    >
      <input
        type="checkbox"
        className="w-6 h-6 bg-[#575555] border-0 data-[state=checked]:bg-[#575555] checked:bg-[url('/images/cookie-checked.svg')]"
        id={id}
        checked={checked}
        disabled={disabled}
        defaultValue={defaultValue}
        onChange={onChange}
      />
      {label}
    </label>
  )
}

const CookiePolicyInfo = ({
  closePopup,
  setCustomizingOn,
}: {
  closePopup: () => void
  setCustomizingOn: (value: boolean) => void
}) => {
  return (
    <>
      <div className="flex flex-col gap-[12px]">
        <PopupHeader>We Value Your Privacy</PopupHeader>
        <PopupParagraph>
          Tracker Boot uses cookies and similar technologies to enhance user experience, improve site performance, and
          analyze usage trends. Some cookies are essential for core functionality, while others help us optimize
          performance and user engagement. You can manage your cookie preferences in settings. For more details, please
          read our&nbsp;
          <PopupLink href="/policies/cookie-policy">Cookie Policy</PopupLink>
          &nbsp;and&nbsp;
          <PopupLink href="/policies/privacy-policy">Privacy Policy</PopupLink>.
        </PopupParagraph>
      </div>
      <div className="flex flex-wrap gap-5">
        <PopupButton
          onClick={() => {
            closePopup()
            saveCookieConsent({
              consent: true,
              essential: true,
              analyticsAndPerformance: true,
            })
          }}
        >
          Accept All Cookies
        </PopupButton>
        <PopupButton onClick={() => setCustomizingOn(true)}>Customize Cookies</PopupButton>
      </div>
    </>
  )
}

const CookiePolicyCustom = ({
  closePopup,
  setCustomizingOn,
}: {
  closePopup: () => void
  setCustomizingOn: (value: boolean) => void
}) => {
  const [analytics, setAnalytics] = useState<boolean>(false)

  return (
    <>
      <div className="flex flex-col gap-[12px]">
        <PopupHeader>Customize Cookie Settings</PopupHeader>
        <PopupParagraph>
          You can enable or disable Analytics & Performance Cookies.
          <br />
          Essential cookies are always enabled to ensure core product functionality and security.
        </PopupParagraph>
        <div className="flex flex-col gap-3">
          <PopupCheckbox id="mandatory" checked={true} disabled={true} label="Essential Cookies (Always Enabled)" />
          <PopupCheckbox
            id="analytics"
            checked={analytics}
            label="Analytics & Performance Cookies"
            onChange={(e) => setAnalytics(e.target.checked)}
          />
        </div>
      </div>
      <div className="flex flex-wrap gap-5">
        <PopupButton onClick={() => setCustomizingOn(false)}>Cancel</PopupButton>
        <PopupButton
          onClick={() => {
            closePopup()
            saveCookieConsent({
              consent: true,
              essential: true,
              analyticsAndPerformance: analytics,
            })
          }}
        >
          Save
        </PopupButton>
      </div>
    </>
  )
}

export const CookiePolicyPopup = () => {
  const [popupOpen, setPopupOpen] = useState(isCookieConsentDone())
  const [isCustomizingOn, setCustomizingOn] = useState<boolean>(false)

  return (
    <div className="z-10 fixed bottom-0 w-full">
      {!popupOpen && (
        <div className="w-full bg-[#2d2b2b] rounded-none shadow-none py-[30px] px-[24px] flex flex-col gap-[24px]">
          <div className="max-w-[1548px] m-auto flex flex-col gap-5">
            {isCustomizingOn ? (
              <CookiePolicyCustom closePopup={() => setPopupOpen(true)} setCustomizingOn={setCustomizingOn} />
            ) : (
              <CookiePolicyInfo closePopup={() => setPopupOpen(true)} setCustomizingOn={setCustomizingOn} />
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default CookiePolicyPopup
